import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Plus, Pencil, Trash2, Building2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => { if (lat && lng) map.flyTo([lat, lng], Math.max(map.getZoom(), 14)); }, [lat, lng, map]);
  return null;
}

function ClickPicker({ onPick }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}

const EMPTY = { name: "", address: "", lat: 3.139, lng: 101.687, is_hq: false };

export default function LocationSettings() {
  const [locations, setLocations] = useState([]);
  const [dialog, setDialog] = useState(null); // {mode:'new'|'edit', id}
  const [form, setForm] = useState(EMPTY);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/locations").then((r) => setLocations(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (query.trim().length < 3) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.get("/geo/search", { params: { q: query } }).then((r) => setSuggestions(r.data)).catch(() => {});
    }, 450);
    return () => clearTimeout(t);
  }, [query]);

  const openNew = () => { setForm(EMPTY); setQuery(""); setSuggestions([]); setDialog({ mode: "new" }); };
  const openEdit = (l) => { setForm({ name: l.name, address: l.address || "", lat: l.lat, lng: l.lng, is_hq: !!l.is_hq }); setQuery(""); setSuggestions([]); setDialog({ mode: "edit", id: l.id }); };

  const pickSuggestion = (s) => {
    setForm((f) => ({ ...f, address: s.name, lat: s.lat, lng: s.lng }));
    setSuggestions([]);
    setQuery("");
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Sila isi nama lokasi"); return; }
    setSaving(true);
    try {
      if (dialog.mode === "edit") await api.put(`/locations/${dialog.id}`, form);
      else await api.post("/locations", form);
      toast.success("Lokasi disimpan");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Padam lokasi "${l.name}"?`)) return;
    try {
      await api.delete(`/locations/${l.id}`);
      toast.success("Lokasi dipadam");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="location-settings">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="font-heading text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Cawangan, Pejabat & Lokasi</CardTitle>
              <CardDescription className="mt-1">Lokasi bertolak untuk tempahan — jarak dikira automatik dari lokasi ini ke destinasi.</CardDescription>
            </div>
            <Button onClick={openNew} className="bg-[#10056E] hover:bg-[#2a17a8] shrink-0" data-testid="add-location-btn">
              <Plus className="h-4 w-4 mr-2" /> Tambah Lokasi
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <MapContainer center={[3.05, 101.65]} zoom={9} scrollWheelZoom={false} style={{ height: 300, width: "100%", zIndex: 0 }} className="rounded-lg">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {locations.map((l) => (
              <CircleMarker key={l.id} center={[l.lat, l.lng]} radius={10}
                pathOptions={{ color: "#fff", weight: 2.5, fillColor: l.is_hq ? "#10056E" : "#198754", fillOpacity: 1 }}>
                <Popup><b>{l.name}</b><br />{l.address}</Popup>
              </CircleMarker>
            ))}
          </MapContainer>
          <div className="space-y-2" data-testid="location-list">
            {locations.length === 0 && <p className="text-sm text-muted-foreground">Tiada lokasi lagi. Tambah ibu pejabat anda dahulu.</p>}
            {locations.map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-3 border rounded-lg p-3" data-testid={`location-row-${l.id}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <MapPin className={`h-4 w-4 mt-0.5 shrink-0 ${l.is_hq ? "text-[#10056E]" : "text-[#198754]"}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold flex items-center gap-2">{l.name}
                      {l.is_hq && <Badge className="bg-[#10056E] hover:bg-[#10056E] text-[10px]">Ibu Pejabat</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{l.address || `${l.lat.toFixed(4)}, ${l.lng.toFixed(4)}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => openEdit(l)} data-testid={`edit-location-${l.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="icon" className="h-8 w-8 text-[#DC3545] border-[#DC3545] hover:bg-red-50" onClick={() => remove(l)} data-testid={`delete-location-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>{dialog?.mode === "edit" ? "Kemas Kini Lokasi" : "Tambah Lokasi Baharu"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nama Lokasi</Label>
              <Input data-testid="location-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cth: Cawangan Johor Bahru" />
            </div>
            <div className="space-y-1.5 relative">
              <Label>Cari Alamat</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="location-search-input" className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Taip alamat / nama tempat..." />
              </div>
              {suggestions.length > 0 && (
                <div className="absolute z-[1000] top-full mt-1 inset-x-0 bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto" data-testid="location-suggestions">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => pickSuggestion(s)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b last:border-0">
                      <MapPin className="h-3 w-3 inline mr-1.5 text-[#10056E]" />{s.name}
                    </button>
                  ))}
                </div>
              )}
              {form.address && <p className="text-xs text-muted-foreground">{form.address}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Kedudukan Pada Peta <span className="text-muted-foreground font-normal">(klik peta untuk laraskan)</span></Label>
              <MapContainer center={[form.lat, form.lng]} zoom={13} scrollWheelZoom={false} style={{ height: 200, width: "100%", zIndex: 0 }} className="rounded-lg">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Recenter lat={form.lat} lng={form.lng} />
                <ClickPicker onPick={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))} />
                <CircleMarker center={[form.lat, form.lng]} radius={10} pathOptions={{ color: "#fff", weight: 2.5, fillColor: "#10056E", fillOpacity: 1 }} />
              </MapContainer>
              <p className="text-[11px] text-muted-foreground font-mono">{form.lat.toFixed(5)}, {form.lng.toFixed(5)}</p>
            </div>
            <div className="flex items-center justify-between border rounded-lg p-3">
              <div>
                <p className="text-sm font-semibold">Ibu Pejabat (HQ)</p>
                <p className="text-xs text-muted-foreground">Lokasi lalai untuk pengiraan jarak tempahan.</p>
              </div>
              <Switch checked={form.is_hq} onCheckedChange={(v) => setForm({ ...form, is_hq: v })} data-testid="location-hq-toggle" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-[#10056E] hover:bg-[#2a17a8]" data-testid="location-save-btn">
              {saving ? "Menyimpan..." : "Simpan Lokasi"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
